import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { 
  UserPlus, 
  Users, 
  Shield, 
  Ban, 
  CheckCircle, 
  Trash2, 
  Loader2,
  Mail,
  Phone,
  Copy,
  Download,
  Key,
  Eye,
  EyeOff
} from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminsPage() {
  const { token, user } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    nom: "",
    prenom: "",
    telephone: ""
  });
  
  // State for credentials modal
  const [credentialsModal, setCredentialsModal] = useState({ open: false, credentials: null });
  const [showPassword, setShowPassword] = useState(false);

  // Check if current user is super admin (admin_igt is legacy role treated as super_admin)
  const isSuperAdmin = user?.role === "super_admin" || user?.role === "admin_igt";

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      const response = await axios.get(`${API_URL}/admins`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdmins(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Accès réservé au Super Administrateur");
      } else {
        toast.error("Erreur lors du chargement des administrateurs");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      const response = await axios.post(`${API_URL}/admins`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Administrateur IGAT créé avec succès");
      setDialogOpen(false);
      setFormData({ email: "", nom: "", prenom: "", telephone: "" });
      fetchAdmins();
      
      // Show credentials modal if credentials are returned
      if (response.data.credentials) {
        setCredentialsModal({
          open: true,
          credentials: {
            ...response.data.credentials,
            nom: `${formData.prenom} ${formData.nom}`
          }
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setCreating(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copié dans le presse-papier`);
  };

  const downloadCredentialsPDF = () => {
    // Generate a simple text file with credentials as fallback
    const creds = credentialsModal.credentials;
    const content = `
INSPECTION GÉNÉRALE DU TRAVAIL
République de Guinée
================================

FICHE D'IDENTIFIANTS DE CONNEXION
Compte Administrateur IGAT

Nom: ${creds.nom}
Fonction: ${creds.role}

IDENTIFIANTS DE CONNEXION
================================
Plateforme: ${creds.platform_url}
Email: ${creds.email}
Mot de passe: ${creds.password}

⚠️ IMPORTANT: Changez votre mot de passe à la première connexion.

Ce document est confidentiel.
    `.trim();
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Identifiants_Admin_${creds.nom.replace(/\s+/g, '_')}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    toast.success("Identifiants téléchargés");
  };

  const handleSuspend = async (adminId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir suspendre ce compte ?")) return;
    
    try {
      await axios.put(`${API_URL}/admins/${adminId}/suspend`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Compte suspendu");
      fetchAdmins();
    } catch (error) {
      toast.error("Erreur lors de la suspension");
    }
  };

  const handleActivate = async (adminId) => {
    try {
      await axios.put(`${API_URL}/admins/${adminId}/activate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Compte réactivé");
      fetchAdmins();
    } catch (error) {
      toast.error("Erreur lors de la réactivation");
    }
  };

  const handleDelete = async (adminId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer définitivement ce compte ?")) return;
    
    try {
      await axios.delete(`${API_URL}/admins/${adminId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Compte supprimé");
      fetchAdmins();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="p-8">
        <Card className="border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="text-red-700">Accès refusé</CardTitle>
            <CardDescription className="text-red-600">
              Cette page est réservée au Super Administrateur (IGT).
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admins-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">
            Gestion des Administrateurs
          </h1>
          <p className="text-muted-foreground mt-1">
            Gérez les comptes administrateurs IGAT
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90" data-testid="create-admin-btn">
              <UserPlus className="w-4 h-4 mr-2" />
              Nouvel Administrateur
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Créer un compte Administrateur IGAT</DialogTitle>
              <DialogDescription>
                Ce compte sera attribué à l'Inspecteur Général Adjoint du Travail.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateAdmin}>
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="prenom">Prénom</Label>
                    <Input
                      id="prenom"
                      value={formData.prenom}
                      onChange={(e) => setFormData({...formData, prenom: e.target.value})}
                      placeholder="Prénom"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="nom">Nom</Label>
                    <Input
                      id="nom"
                      value={formData.nom}
                      onChange={(e) => setFormData({...formData, nom: e.target.value})}
                      placeholder="Nom"
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="email@igt.gov.gn"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="telephone">Téléphone (optionnel)</Label>
                  <Input
                    id="telephone"
                    value={formData.telephone}
                    onChange={(e) => setFormData({...formData, telephone: e.target.value})}
                    placeholder="+224 6XX XX XX XX"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Annuler
                </Button>
                <Button type="submit" disabled={creating}>
                  {creating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Création...
                    </>
                  ) : (
                    "Créer le compte"
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <Users className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Total Administrateurs</p>
                <p className="text-2xl font-bold">{admins.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Comptes Actifs</p>
                <p className="text-2xl font-bold text-green-600">
                  {admins.filter(a => a.status === "actif").length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-red-100 flex items-center justify-center">
                <Ban className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Comptes Suspendus</p>
                <p className="text-2xl font-bold text-red-600">
                  {admins.filter(a => a.status === "suspendu").length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Admins Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Administrateurs IGAT
          </CardTitle>
          <CardDescription>
            Liste des comptes Inspecteur Général Adjoint du Travail
          </CardDescription>
        </CardHeader>
        <CardContent>
          {admins.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              Aucun administrateur IGAT créé
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nom</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Téléphone</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Date de création</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {admins.map((admin) => (
                  <TableRow key={admin.id}>
                    <TableCell className="font-medium">
                      {admin.prenom} {admin.nom}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm">
                        <Mail className="w-3 h-3" />
                        {admin.email}
                      </div>
                    </TableCell>
                    <TableCell>
                      {admin.telephone ? (
                        <div className="flex items-center gap-1 text-sm">
                          <Phone className="w-3 h-3" />
                          {admin.telephone}
                        </div>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={admin.status === "actif" ? "default" : "destructive"}
                        className={admin.status === "actif" ? "bg-green-100 text-green-700" : ""}
                      >
                        {admin.status === "actif" ? "Actif" : "Suspendu"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {new Date(admin.created_at).toLocaleDateString("fr-FR")}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        {admin.status === "actif" ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleSuspend(admin.id)}
                            className="text-orange-600 hover:text-orange-700"
                          >
                            <Ban className="w-4 h-4" />
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleActivate(admin.id)}
                            className="text-green-600 hover:text-green-700"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(admin.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Credentials Modal */}
      <Dialog open={credentialsModal.open} onOpenChange={(open) => setCredentialsModal({ open, credentials: credentialsModal.credentials })}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <Key className="w-5 h-5" />
              Compte Administrateur Créé
            </DialogTitle>
            <DialogDescription>
              Voici les identifiants de connexion pour le nouvel administrateur IGAT.
              Un email avec ces informations et un PDF a également été envoyé.
            </DialogDescription>
          </DialogHeader>
          
          {credentialsModal.credentials && (
            <div className="space-y-4">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <h4 className="font-semibold text-emerald-800 mb-3">
                  {credentialsModal.credentials.nom}
                </h4>
                <p className="text-sm text-emerald-700">
                  Fonction : {credentialsModal.credentials.role}
                </p>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <p className="text-xs text-slate-500">Plateforme</p>
                    <p className="font-mono text-sm">{credentialsModal.credentials.platform_url}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(credentialsModal.credentials.platform_url, "URL")}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <p className="text-xs text-slate-500">Email (identifiant)</p>
                    <p className="font-mono text-sm">{credentialsModal.credentials.email}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(credentialsModal.credentials.email, "Email")}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div>
                    <p className="text-xs text-amber-600">Mot de passe temporaire</p>
                    <p className="font-mono text-sm font-bold text-amber-700">
                      {showPassword ? credentialsModal.credentials.password : "••••••••••••"}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(credentialsModal.credentials.password, "Mot de passe")}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
              
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-700">
                  <strong>Important :</strong> L'administrateur devra changer son mot de passe lors de sa première connexion.
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={downloadCredentialsPDF}>
              <Download className="w-4 h-4 mr-2" />
              Télécharger
            </Button>
            <Button onClick={() => setCredentialsModal({ open: false, credentials: null })}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
